/* eslint-disable require-jsdoc */
import * as focusTrap from 'focus-trap';
import classNames from 'classnames';
import { clearWindowsScrollPosition } from '../../utilities/scrolling-helper';
import DatePicker from './date-picker';
import { format } from 'date-fns';
import FullScreenDialog from './full-screen-dialog';
import Icon from './icon';
import moment from 'moment';
import PropTypes from 'prop-types';
import Select from './select';
import { GENRE_GROUPS, PRICE_RANGE } from 'config/event';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ShowFinder = ({ isShowFinderDialogActive, setShowFinderDialogActive }) => {
    const [searchParams] = useSearchParams();
    const [genre, setGenre] = useState('');
    const [price, setPrice] = useState('');
    const [activeDatePicker, setActiveDatePicker] = useState(null);
    // hack to prevent dialog from reopening right after deactivating it
    const [canOpenDatePicker, setCanOpenDatePicker] = useState(true);

    const focusTrapRefs = useRef(null);
    const mobileDialogFocusTrapRef = useRef(null);

    const [from, setFrom] = useState(undefined);
    const [to, setTo] = useState(undefined);

    const navigate = useNavigate();

    const isDatePickerActive = activeDatePicker !== null;

    const getQueryValue = (key) => {
        return !searchParams.get(key) || searchParams.get(key) === '-' ? '' : decodeURIComponent(searchParams.get(key));
    };

    const validDate = (date) => {
        return date && moment(date).isValid() ? new Date(date) : '';
    };

    const generateDateValue = (startDate, endDate, dateType = 'both') => {
        if (dateType === 'both') {
            setFrom((startDate));
            setTo(endDate);
        }
        if (dateType === 'start') {
            setFrom(startDate);
        }
        if (dateType === 'end') {
            setTo(endDate);
        }
    };

    const dateClickHanlder = (day, dateType) => {
        if (dateType === 'start') {
            generateDateValue(day, null, 'start');
        } else {
            generateDateValue(null, day, 'end');
        }
        focusTrapRefs.current[dateType].deactivate();
    };

    const openDatePicker = (dateType) => {
        // Check for focus trap deactivate timeout
        if (canOpenDatePicker) {
            setActiveDatePicker(dateType);
            setCanOpenDatePicker(false);
        }
    };

    const dateRangeClickHanlder = (rangeType, hideOnSelect) => {
        const today = new Date();
        // eslint-disable-next-line no-magic-numbers
        const sevenDaysLater = moment(today).add(7, 'd').toDate();
        const endOfWeek = moment(today).endOf('isoweek').toDate();
        const endOfMonth = moment(today).endOf('month').toDate();

        switch (rangeType) {
            case 'next-seven-days':
                generateDateValue(today, sevenDaysLater);
                break;
            case 'this-week':
                generateDateValue(today, endOfWeek);
                break;
            case 'this-month':
                generateDateValue(today, endOfMonth);
                break;
            case 'any-dates':
                clearDatesHandler();
                break;
            default:
                break;
        }

        if (hideOnSelect) {
            // just deactivate all datepickers, we don't know which one it came from
            closeAllDatepickers();
        }
    };

    // Deactivate focus traps for all datepickers
    const closeAllDatepickers = () => {
        Object.values(focusTrapRefs.current).forEach((trap) => trap.deactivate());
    };

    const clearDatesHandler = () => generateDateValue(undefined, undefined);

    const formatDate = () => {
        let formattedEndDate = '',
            formattedStartDate = '-';

        if (from) {
            formattedStartDate = moment(from).format('YYYY-MM-DD');
        }
        if (to) {
            formattedEndDate = `,${moment(to).format('YYYY-MM-DD')}`;
        }

        return formattedStartDate + formattedEndDate;
    };

    const submitHanlder = (event) => {
        event.preventDefault();
        const date = formatDate();
        // Deactivate all focus traps
        closeAllDatepickers();
        mobileDialogFocusTrapRef.current.deactivate();

        // Clear session Storage for remembering scrolling state on event result page
        clearWindowsScrollPosition();

        navigate({
            pathname: '/search/events',
            search: `?genre=${genre || '-'}&date=${encodeURIComponent(date)}&price=${price || '-'}`
        });
    };

    const onSelectChange = (event, name) => {
        if (name === 'genre') {
            setGenre(event.target.value);
        } else {
            setPrice(event.target.value);
        }
    };

    // Init focus traps for datepickers and mobile show finder dialog
    useEffect(() => {
        // focus trap keys correspond to values of activeDatePicker
        const traps = {};
        for (const t of ['range', 'start', 'end']) {
            traps[t] = focusTrap.createFocusTrap(document.getElementById(`date-picker-${t}`), {
                // close dialog when clicking outside the focus trap
                onDeactivate: () => {
                    setActiveDatePicker(null);
                    // hack to prevent dialog from re-opening because the focus-trap library doesn't prevent the click event when deactivating the focus trap
                    setTimeout(() => setCanOpenDatePicker(true), 250);
                },
                clickOutsideDeactivates: true,
            });
        }
        focusTrapRefs.current = traps;
        mobileDialogFocusTrapRef.current = focusTrap.createFocusTrap(document.getElementById('show-finder-dialog'), {
            onDeactivate: () => {
                setShowFinderDialogActive(false);
            },
        });
    }, []);

    useEffect(() => {
        const updatedGenre = getQueryValue('genre');
        const updatedDate = getQueryValue('date');
        const updatedPrice = getQueryValue('price');
        if (updatedDate) {
            const [startDate, endDate] = updatedDate.split(',');
            generateDateValue(validDate(startDate), validDate(endDate), 'both');
        }

        if (updatedGenre !== genre) setGenre(updatedGenre);
        if (updatedPrice !== price) setPrice(updatedPrice);
    }, [searchParams]);

    // activate focus trap and move focus to first button
    // Needs to be done in useEffect instead of dateClickHanlder() because the DOM has not been updated yet after setting React state
    useEffect(() => {
        if (activeDatePicker) {
            focusTrapRefs.current[activeDatePicker].activate();
        }
    }, [activeDatePicker]);

    // activate focus trap for mobile show finder dialog
    // Needs to be done in useEffect because the DOM has not been updated yet after setting React state
    useEffect(() => {
        if (isShowFinderDialogActive) {
            mobileDialogFocusTrapRef.current.activate();
        }
    }, [isShowFinderDialogActive]);

    // Render
    const renderDateRangeButtonsGroup = (hideOnSelect = false) => (
        <div className="date-ranges">
            <button className="button" type="button" onClick={() => dateRangeClickHanlder('next-seven-days', hideOnSelect)}>next 7 days</button>
            <button className="button" type="button" onClick={() => dateRangeClickHanlder('this-week', hideOnSelect)}>this weekend</button>
            <button className="button" type="button" onClick={() => dateRangeClickHanlder('this-month', hideOnSelect)}>this month</button>
            <button className="button" type="button" onClick={() => dateRangeClickHanlder('any-dates', hideOnSelect)}>any dates</button>
        </div>
    );

    const renderDateInput = (inputKey, inputValue) => {
        return (
            <div className="date-input-wrapper">
                <div className="input-wrapper with-icon">
                    <button
                        type="button"
                        className="input || date-picker-input"
                        onClick={() => openDatePicker(inputKey)}
                        // event date start/end
                        aria-label={toggleDatePickerLabel(`Choose ${inputKey} date`, formatSrDate(inputValue))}
                        aria-expanded={activeDatePicker === inputKey}
                        aria-controls={activeDatePicker === inputKey ? `date-picker-${inputKey}` : undefined}
                    >
                        {formatDisplayDate(inputValue) || 'any date'}
                    </button>
                    <span className="label-icon">
                        <Icon name="calender-add" />
                    </span>
                </div>
            </div>
        );
    };

    const renderDateInputGroup = () => (
        <React.Fragment>
            <div className="field-group inline">
                <span className="label">from</span>
                {renderDateInput('start', from)}
            </div>
            <div className="field-group inline">
                <span className="label">to</span>
                {renderDateInput('end', to)}
            </div>
        </React.Fragment>
    );

    // only used on desktop
    const renderDateRangePicker = () => {
        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
                style={{ display: activeDatePicker === 'range' ? 'unset' : 'none' }}
                id="date-picker-range"
                className="date-picker-wrapper"
                role="dialog"
                aria-label="Choose date range"
            >
                {renderDateRangeButtonsGroup()}
                <DatePicker
                    mode="range"
                    numberOfMonths={2}
                    selected={{ from, to }}
                    onSelect={(dateRange) => generateDateValue(dateRange.from, dateRange.to)}
                    disabled={[{ before: new Date() }]}
                />
                <button className="button primary inverse || clear-dates" type="button" onClick={clearDatesHandler}>clear dates</button>
                {/* add a close button because we are focus trapped */}
                <button
                    type="button"
                    className="no-border || btn-close"
                    onClick={() => focusTrapRefs.current.range.deactivate()}
                >
                    <Icon className="icon" name="close" />
                </button>
                {/* For screen readers to announce changes to the selected date range */}
                <span
                    className="sr-only"
                    aria-live="polite"
                    aria-atomic="true"
                >Selected event date: {formatSrDateRange(from, to)}</span>
            </div>
        );
    };

    const renderDateRangeInput = () => (
        <div className="field-group inline">
            <span className="label">on</span>
            <div className="date-input-wrapper">
                <div className="input-wrapper with-icon">
                    <button
                        type="button"
                        className="input || date-picker-input"
                        onClick={() => openDatePicker('range')}
                        aria-label={toggleDatePickerLabel('Choose date', formatSrDateRange(from, to))}
                        aria-expanded={isDatePickerActive}
                        aria-controls={isDatePickerActive ? 'date-picker-range' : undefined}
                    >
                        {formatDisplayDateRange(from, to) || 'any date'}
                    </button>
                    <span className="label-icon">
                        <Icon name="chevron" />
                    </span>
                </div>
                {renderDateRangePicker()}
            </div>
        </div>
    );

    const renderSearchForm = (isShowFinderDialog = false) => {
        const showFinderClasses = classNames('show-finder', 'form', {
            'constrain-width large': !isShowFinderDialog,
            'show-finder-dialog ': isShowFinderDialog
        });

        return (
            <form className={showFinderClasses} action="/search/events" method="GET" onSubmit={submitHanlder}>
                <fieldset>
                    <legend className="sr-only">Find an event</legend>
                    <div className="label title">I&apos;m looking for</div>
                    <div>
                        <label htmlFor="event-genre" className="sr-only">Event Genre</label>
                        <Select
                            id="event-genre"
                            name="genre"
                            options={GENRE_GROUPS}
                            value={genre}
                            onChangeHanlder={(event) => onSelectChange(event, 'genre')}
                        />
                    </div>
                    {!isShowFinderDialog && renderDateRangeInput()}
                    {isShowFinderDialog && renderDateInputGroup()}
                    <div className="field-group inline">
                        <span className="label">for</span>
                        <label htmlFor="event-price" className="sr-only">Event Price</label>
                        <Select id="event-price" name="price" options={PRICE_RANGE} value={price} onChangeHanlder={(event) => onSelectChange(event, 'price')} />
                    </div>
                    {isShowFinderDialog && <button className="button secondary || clear-dates" type="button" onClick={clearDatesHandler}>Clear dates</button>}
                    <button className={`button ${isShowFinderDialog ? 'primary inverse' : 'secondary'}`} type="submit">Find</button>
                </fieldset>
            </form>
        );
    };

    // only used on mobile
    const renderDatePicker = (name) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            style={{ display: activeDatePicker === name ? 'unset' : 'none' }}
            className="date-picker-wrapper"
            id={`date-picker-${name}`}
            role="dialog"
            aria-label={`Choose ${name} date`}
        >
            {renderDateRangeButtonsGroup(true)}
            <DatePicker
                mode="single"
                selected={name === 'start' ? from : to}
                disabled={[{ before: new Date() }]}
                onSelect={(day) => dateClickHanlder(day, name)}
            />
            <button className="button primary" type="button" onClick={() => focusTrapRefs.current[name].deactivate()}>Close</button>
        </div>
    );

    const renderShowFinderDialog = (isActive) => {
        const dialogStyle = {
            display: isActive ? 'flex' : 'none',
        };

        const formStyle = {
            display: isDatePickerActive ? 'none' : 'unset',
        };

        return (
            <FullScreenDialog
                id="show-finder-dialog"
                aria-label="Search for an event"
                extraClass="theme-grey"
                isActive={isActive}
                style={dialogStyle}
            >
                <button
                    className="button secondary no-border || action-button"
                    onClick={() => mobileDialogFocusTrapRef.current.deactivate()}
                    style={formStyle}
                >
                    <Icon className="icon" name="close" />
                </button>
                <div style={formStyle}>
                    {renderSearchForm(true)}
                </div>
                {renderDatePicker('start')}
                {renderDatePicker('end')}
                {/* For screen readers to announce changes to the selected date range */}
                <span
                    className="sr-only"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    Selected event date: {formatSrDateRange(from, to)}
                </span>
            </FullScreenDialog>
        );
    };

    const renderFindAnEventButton = () => (
        <button
            className="button secondary || find-event-banner"
            onClick={() => setShowFinderDialogActive(true)}
            aria-expanded={isShowFinderDialogActive}
            aria-controls="show-finder-dialog"
        >
            Find an event
            <span className="label-icon">
                <Icon name="left-arrow" />
            </span>
        </button>
    );

    return (
        <div className="show-finder-wrapper">
            {renderFindAnEventButton()}
            {renderSearchForm()}
            {renderShowFinderDialog(isShowFinderDialogActive)}
        </div>
    );
};

ShowFinder.propTypes = {
    isShowFinderDialogActive: PropTypes.bool.isRequired,
    setShowFinderDialogActive: PropTypes.func.isRequired
};

export default ShowFinder;

// Abbreviated readable date label
function formatDisplayDate(date) {
    if (date) {
        return format(date, 'dd/MM/yyyy');
    }

    return '';
}

function formatDisplayDateRange(from, to) {
    const dates = [];
    if (from) {
        dates.push(formatDisplayDate(from));
    }
    if (to) {
        dates.push(formatDisplayDate(to));
    }

    return dates.join('-') || '';
}

// Screen reader friendly date label
function formatSrDate(date) {
    if (date) {
        return format(date, 'EEEE, MMMM do yyyy');
    }

    return '';
}

// Screen reader friendly date range label
function formatSrDateRange(from, to) {
    const dates = [];
    if (from) {
        dates.push(formatSrDate(from));
    }
    if (to) {
        dates.push(formatSrDate(to));
    }

    return dates.join(' to ') || '';
}

// Build an ARIA label for date picker toggle button
function toggleDatePickerLabel(label, date) {
    const parts = [label];
    if (date) {
        parts.push(date);
    }

    return parts.join(', ');
}
